import React from "react";
import { useDispatch } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { loginUser } from "../store/UserSlice";
import { AppDispatch } from "../store/store";
import { loginSchema, LoginFormData } from "../schemas/auth.schema";
import Input from "../components/form/Input";
import Button from "../components/form/Button";
import { ArrowLeft } from "heroicons-react";

export default function Login() {
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
      rememberMe: false,
    },
    mode: "onSubmit",
  });

  const onSubmit = async (data: LoginFormData) => {
    const result = await dispatch(loginUser(data));

    if (loginUser.fulfilled.match(result) && result.payload) {
      toast.success("Logged In successfully");

      const slugData = localStorage.getItem("SetSlug");

      // If user came from a program page
      if (slugData) {
        navigate(`/program/${slugData}`);
        return;
      }

      const loginData = localStorage.getItem("loginData");

      if (loginData) {
        try {
          const parsedData = JSON.parse(loginData);

          if (parsedData?.user?.isAdmin === true) {
            navigate("/admin/dashboard");
          } else {
            navigate("/bookings");
          }
        } catch (error) {
          console.error("Unable to parse login data:", error);
          navigate("/bookings");
        }

        return;
      }

      // Default redirect
      navigate("/bookings");
    }
  };

  return (
    <div className="flex flex-col lg:flex-row items-center">
      {/* Left Image */}
      <div className="hidden lg:flex w-1/2">
        <img
          src="./images/loginpic.jpg"
          className="object-cover w-full h-screen"
          alt="sideimage"
        />
      </div>

      {/* Login Section */}
      <div className="w-full lg:w-1/2 p-8 min-h-screen flex flex-col justify-center relative">
        <div className="absolute top-8 left-8">
          <Link to="/" className="text-[#037897] font-gilroy-medium text-sm flex items-center">
            <ArrowLeft className="w-4 inline-block mr-2" /> Back to Home
          </Link>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="w-full">
          <div className="max-w-2xl m-auto">
            {/* Header */}
            <div className="text-center">
              <Link to="/" className="inline-block">
                <img
                  src="./images/bluelogo.png"
                  alt="Logo"
                  className="mx-auto mb-6"
                />
              </Link>

              <h2 className="mb-2 font-gilroy text-[#263238] font-semibold text-[26px] md:text-[30px]">
                Log in to your account
              </h2>

              <p className="text-gray-7 text-[#78909C] font-gilroy text-[18px]">
                Welcome back! Sign in to access your account and manage your healthcare needs.
              </p>
            </div>

            {/* Email */}
            <div className="pt-10">
              <Input
                id="email"
                label="Email Address"
                type="email"
                placeholder="Enter your email"
                autoComplete="email"
                error={errors.email?.message}
                {...register("email")}
              />
            </div>

            {/* Password */}
            <div className="pt-8">
              <Input
                id="password"
                label="Password"
                type="password"
                placeholder="Enter your password"
                autoComplete="current-password"
                showPasswordToggle
                error={errors.password?.message}
                {...register("password")}
              />
            </div>

            {/* Remember Me */}
            <div className="flex flex-row mt-4 font-gilroy-medium text-[#263238]">
              <div className="flex">
                <input
                  type="checkbox"
                  id="rememberMe"
                  {...register("rememberMe")}
                  className="peer"
                />

                <label
                  htmlFor="rememberMe"
                  className="flex items-center cursor-pointer"
                >
                  <span className="ml-2 text-[#263238]">
                    Remember me
                  </span>
                </label>
              </div>
            </div>

            {/* Login Button */}
            <Button
              isLoading={isSubmitting}
              loadingText="Logging in..."
            >
              Login
            </Button>

            {/* Register */}
            <h4 className="p-4 mt-4 text-center">
              Don't have an account?{" "}
              <Link
                to="/register"
                className="text-[#037897]"
              >
                Register
              </Link>
            </h4>
          </div>
        </form>
      </div>
    </div>
  );
}